
import { useState } from 'react';

type ServiceTier = 'timber-inventory' | 'forest-health' | 'lidar-survey' | 'pre-acquisition' | 'post-storm';

interface Deliverable {
  name: string;
  description: string;
  included: boolean;
}

interface ServiceDeliverables {
  id: ServiceTier;
  name: string;
  tagline: string;
  icon: string;
  color: string;
  deliverables: Deliverable[];
}

const serviceDeliverables: ServiceDeliverables[] = [
  {
    id: 'timber-inventory',
    name: 'Timber Inventory & Stand Mapping',
    tagline: 'RGB aerial survey for stand-level planning',
    icon: 'ri-plant-line',
    color: 'emerald',
    deliverables: [
      { name: 'High-Resolution Orthomosaic', description: 'Stitched aerial imagery at 1-2 cm/pixel resolution', included: true },
      { name: 'Stand Boundary Delineation', description: 'GIS-ready polygons for each distinct stand', included: true },
      { name: 'Density Classification Map', description: 'Visual density zones (sparse, moderate, dense)', included: true },
      { name: 'Access Route Mapping', description: 'Existing roads, trails, and potential access points', included: true },
      { name: 'Species Mix Estimation', description: 'Dominant species identification by stand', included: true },
      { name: 'Downloadable GIS Files', description: 'Shapefiles, KML, and GeoTIFF formats', included: true },
      { name: 'PDF Summary Report', description: 'Overview with key findings and maps', included: true },
      { name: 'Multispectral Health Analysis', description: 'NDVI and stress detection layers', included: false },
      { name: 'Canopy Height Model', description: 'LiDAR-derived height measurements', included: false },
      { name: 'Volume & Biomass Estimates', description: 'Quantified timber volume calculations', included: false },
    ],
  },
  {
    id: 'forest-health',
    name: 'Forest Health & Stress Analysis',
    tagline: 'Multispectral imaging for early problem detection',
    icon: 'ri-heart-pulse-line',
    color: 'amber',
    deliverables: [
      { name: 'High-Resolution Orthomosaic', description: 'Stitched aerial imagery at 1-2 cm/pixel resolution', included: true },
      { name: 'NDVI Vegetation Index Map', description: 'Color-coded plant health visualization', included: true },
      { name: 'Stress Detection Overlay', description: 'Highlighted areas showing decline or stress', included: true },
      { name: 'Disease & Pest Risk Zones', description: 'Flagged areas requiring ground inspection', included: true },
      { name: 'Drought Stress Assessment', description: 'Water stress indicators across the property', included: true },
      { name: 'Die-Off & Mortality Mapping', description: 'Dead or dying tree identification', included: true },
      { name: 'Comparative Analysis', description: 'Before/after comparison if prior data exists', included: true },
      { name: 'Downloadable GIS Files', description: 'Shapefiles, KML, and GeoTIFF formats', included: true },
      { name: 'PDF Summary Report', description: 'Overview with key findings and recommendations', included: true },
      { name: 'Canopy Height Model', description: 'LiDAR-derived height measurements', included: false },
      { name: 'Volume & Biomass Estimates', description: 'Quantified timber volume calculations', included: false },
    ],
  },
  {
    id: 'lidar-survey',
    name: 'LiDAR Forestry Survey',
    tagline: 'Precision 3D data for volume and terrain modeling',
    icon: 'ri-radar-line',
    color: 'sky',
    deliverables: [
      { name: 'High-Resolution Orthomosaic', description: 'Stitched aerial imagery at 1-2 cm/pixel resolution', included: true },
      { name: 'Digital Terrain Model (DTM)', description: 'Bare-earth elevation surface', included: true },
      { name: 'Digital Surface Model (DSM)', description: 'Top-of-canopy elevation surface', included: true },
      { name: 'Canopy Height Model (CHM)', description: 'Tree height calculations across the property', included: true },
      { name: 'Point Cloud Data', description: 'Raw LAS/LAZ files for advanced analysis', included: true },
      { name: 'Volume & Biomass Estimates', description: 'Quantified timber volume by stand', included: true },
      { name: 'Slope & Aspect Analysis', description: 'Terrain characteristics for harvest planning', included: true },
      { name: 'Drainage & Hydrology Mapping', description: 'Water flow patterns and wet areas', included: true },
      { name: 'Stand Boundary Delineation', description: 'GIS-ready polygons for each distinct stand', included: true },
      { name: 'Downloadable GIS Files', description: 'Shapefiles, KML, GeoTIFF, and LAS formats', included: true },
      { name: 'Comprehensive PDF Report', description: 'Detailed findings with visualizations', included: true },
    ],
  },
  {
    id: 'pre-acquisition',
    name: 'Pre-Acquisition & Valuation Survey',
    tagline: 'Independent data for informed investment decisions',
    icon: 'ri-file-search-line',
    color: 'violet',
    deliverables: [
      { name: 'High-Resolution Orthomosaic', description: 'Stitched aerial imagery at 1-2 cm/pixel resolution', included: true },
      { name: 'Stand Boundary Delineation', description: 'GIS-ready polygons for each distinct stand', included: true },
      { name: 'Density & Species Assessment', description: 'Stand composition and stocking levels', included: true },
      { name: 'Access & Infrastructure Review', description: 'Roads, boundaries, and improvements', included: true },
      { name: 'NDVI Health Screening', description: 'Basic vegetation health overview', included: true },
      { name: 'Encroachment & Boundary Check', description: 'Visible boundary issues or encroachments', included: true },
      { name: 'Risk Factor Summary', description: 'Flagged concerns for due diligence', included: true },
      { name: 'Downloadable GIS Files', description: 'Shapefiles, KML, and GeoTIFF formats', included: true },
      { name: 'Investor-Ready PDF Report', description: 'Professional summary for stakeholders', included: true },
      { name: 'Executive Summary Option', description: 'One-page highlights for decision-makers', included: true },
    ],
  },
  {
    id: 'post-storm',
    name: 'Post-Storm & Insurance Assessment',
    tagline: 'Rapid damage documentation for claims and recovery',
    icon: 'ri-thunderstorms-line',
    color: 'rose',
    deliverables: [
      { name: 'High-Resolution Orthomosaic', description: 'Stitched aerial imagery at 1-2 cm/pixel resolution', included: true },
      { name: 'Damage Zone Mapping', description: 'Delineated areas of impact by severity', included: true },
      { name: 'Blowdown & Breakage Count', description: 'Estimated affected tree counts', included: true },
      { name: 'Before/After Comparison', description: 'Visual change detection if prior data exists', included: true },
      { name: 'Loss Quantification', description: 'Estimated acreage and volume affected', included: true },
      { name: 'Access Impact Assessment', description: 'Road blockages and accessibility issues', included: true },
      { name: 'Salvage Priority Zones', description: 'Recommended areas for immediate recovery', included: true },
      { name: 'Downloadable GIS Files', description: 'Shapefiles, KML, and GeoTIFF formats', included: true },
      { name: 'Insurance-Ready PDF Report', description: 'Documentation formatted for claims', included: true },
      { name: 'Expedited Delivery', description: 'Priority turnaround for time-sensitive claims', included: true },
    ],
  },
];

const colorClasses: Record<string, { bg: string; border: string; text: string; light: string }> = {
  emerald: { bg: 'bg-emerald-600', border: 'border-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50' },
  amber: { bg: 'bg-amber-600', border: 'border-amber-500', text: 'text-amber-600', light: 'bg-amber-50' },
  sky: { bg: 'bg-sky-600', border: 'border-sky-500', text: 'text-sky-600', light: 'bg-sky-50' },
  violet: { bg: 'bg-violet-600', border: 'border-violet-500', text: 'text-violet-600', light: 'bg-violet-50' },
  rose: { bg: 'bg-rose-600', border: 'border-rose-500', text: 'text-rose-600', light: 'bg-rose-50' },
};

export default function DeliverablesBreakdown() {
  const [activeService, setActiveService] = useState<ServiceTier>('timber-inventory');

  const currentService = serviceDeliverables.find(s => s.id === activeService) || serviceDeliverables[0];
  const colors = colorClasses[currentService.color];

  return (
    <section className="py-20 bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-stone-900 mb-4">What&apos;s Included</h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Every service tier includes a comprehensive set of deliverables. Select a service to see exactly what you&apos;ll receive.
          </p>
        </div>

        {/* Service Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {serviceDeliverables.map(service => {
            const isActive = activeService === service.id;
            const serviceColors = colorClasses[service.color];
            return (
              <button
                key={service.id}
                onClick={() => setActiveService(service.id)}
                className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                  isActive
                    ? `${serviceColors.bg} text-white shadow-md`
                    : 'bg-white text-stone-700 border border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${service.icon} text-base`}></i>
                </span>
                <span className="hidden sm:inline">{service.name.split('&')[0].trim()}</span>
                <span className="sm:hidden">{service.name.split('&')[0].trim().split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Active Service Details */}
        <div className={`rounded-xl border-2 ${colors.border} bg-white overflow-hidden shadow-lg`}>
          {/* Header */}
          <div className={`${colors.light} px-8 py-6 border-b ${colors.border}`}>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center`}>
                <i className={`${currentService.icon} text-2xl text-white`}></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-stone-900">{currentService.name}</h3>
                <p className="text-stone-600 text-sm mt-1">{currentService.tagline}</p>
              </div>
            </div>
          </div>

          {/* Deliverables Grid */}
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-4">
              {currentService.deliverables.map((deliverable, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-4 rounded-lg transition-colors ${
                    deliverable.included ? 'bg-stone-50' : 'bg-stone-100/50 opacity-60'
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      deliverable.included ? `${colors.bg} text-white` : 'bg-stone-300 text-stone-500'
                    }`}
                  >
                    <i className={`${deliverable.included ? 'ri-check-line' : 'ri-close-line'} text-sm`}></i>
                  </div>
                  <div>
                    <div className={`font-semibold text-sm ${deliverable.included ? 'text-stone-900' : 'text-stone-500'}`}>
                      {deliverable.name}
                    </div>
                    <div className={`text-xs mt-0.5 ${deliverable.included ? 'text-stone-600' : 'text-stone-400'}`}>
                      {deliverable.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Included Count */}
            <div className="mt-8 pt-6 border-t border-stone-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${colors.light} flex items-center justify-center`}>
                  <i className={`ri-checkbox-circle-line text-xl ${colors.text}`}></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-stone-900">
                    {currentService.deliverables.filter(d => d.included).length}
                  </div>
                  <div className="text-xs text-stone-500">Deliverables Included</div>
                </div>
              </div>
              <div className="text-sm text-stone-600 max-w-md">
                All deliverables are provided in industry-standard formats. Custom formats or additional analysis available upon request.
              </div>
            </div>
          </div>
        </div>

        {/* Common Across All */}
        <div className="mt-10 grid sm:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-5 border border-stone-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <i className="ri-cloud-line text-lg text-stone-600"></i>
              </div>
              <span className="font-semibold text-stone-900 text-sm">Cloud Delivery</span>
            </div>
            <p className="text-xs text-stone-600 leading-relaxed">
              All files delivered via secure cloud link. Download anytime, share with your team or consultants.
            </p>
          </div>

          <div className="bg-white rounded-lg p-5 border border-stone-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-lg text-stone-600"></i>
              </div>
              <span className="font-semibold text-stone-900 text-sm">Support Included</span>
            </div>
            <p className="text-xs text-stone-600 leading-relaxed">
              Questions about your data? We provide guidance on interpreting results and using deliverables.
            </p>
          </div>

          <div className="bg-white rounded-lg p-5 border border-stone-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <i className="ri-archive-line text-lg text-stone-600"></i>
              </div>
              <span className="font-semibold text-stone-900 text-sm">Data Retention</span>
            </div>
            <p className="text-xs text-stone-600 leading-relaxed">
              Your data is archived for 12 months. Extended retention and historical comparison available.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
